import { BriefPage } from './BriefPage';

const FOOTER = 'WinIQ / Meeting intelligence';

const CONFIRMATION_LABELS = {
  confirmed: 'Confirmed',
  supported: 'Supported',
  hypothesis: 'Hypothesis',
  conflict: 'Conflict',
  unknown: 'Unknown',
};

const getMeetingLine = ({
  prospect_name: prospectName,
  seller_name: sellerName,
  seller_company: sellerCompany,
  meeting_date: meetingDate,
  meeting_duration_minutes: duration,
  booked_status: bookedStatus,
}) =>
  [
    `PROSPECT ${prospectName}`,
    (sellerName || sellerCompany) &&
      `Seller: ${[sellerName, sellerCompany].filter(Boolean).join(', ')}`,
    `Meeting: ${[meetingDate, `${duration} minutes`].filter(Boolean).join(', ')}`,
    `Status: ${bookedStatus === 'booked' ? 'Booked' : 'Proposed'}`,
  ]
    .filter(Boolean)
    .join('  |  ');

const List = ({ items }) => (
  <ul className="wq-list">
    {items.map((item, idx) => (
      <li key={idx}>{item}</li>
    ))}
  </ul>
);

export const PrecisionMeetingBrief = ({ brief }) => {
  const {
    meeting_metadata: metadata,
    meeting_objective: objective,
    primary_accelerator: accelerator,
    boundaries = [],
    transcript_evidence: evidence = [],
    data_reconciliation: reconciliation = [],
    recommended_opening: opening,
    agenda = [],
    discovery_questions: questions = [],
    accelerator_transition: transition,
    success_criteria: success,
    objection_pivots: pivots = [],
    closing_language: closing,
    follow_up_plan: followUp = [],
    confidence_and_sources: confidence,
    blocking_gaps: gaps = [],
    additional_sections: additionalSections = [],
  } = brief;

  const vertical = metadata.industry_vertical
    ? `${metadata.industry_vertical} `
    : '';
  const databaseRows = reconciliation.filter(({ data_says: says }) => says);
  const notes = [
    confidence.call_date && `Call on ${confidence.call_date}.`,
    confidence.transcript_coverage,
    confidence.quoted_segments?.length > 0 &&
      `Quotes: segments ${confidence.quoted_segments.join(', ')}.`,
    confidence.data_sources?.length > 0 &&
      `Data used: ${confidence.data_sources.join('; ')}.`,
    confidence.not_established?.length > 0 &&
      `Not established: ${confidence.not_established.join('; ')}.`,
    confidence.notes,
  ].filter(Boolean);

  return (
    <>
      <BriefPage
        kicker="Meeting intelligence brief"
        title={`Turn a booked appointment into a prepared ${vertical}conversation.`}
        footer={FOOTER}
        pageNumber={1}
      >
        <p className="wq-line">{getMeetingLine(metadata)}</p>
        <table className="wq-kv">
          <tbody>
            <tr>
              <td>Meeting objective</td>
              <td>{objective}</td>
            </tr>
            <tr>
              <td>Primary accelerator</td>
              <td>
                {accelerator.name}: {accelerator.description}
              </td>
            </tr>
            {boundaries.length > 0 && (
              <tr>
                <td>Boundary</td>
                <td>{boundaries.join(' ')}</td>
              </tr>
            )}
          </tbody>
        </table>
        <h2 className="wq-h">1 | What the prospect told us</h2>
        {confidence.call_date && (
          <p className="wq-note">
            From the booking call on {confidence.call_date}.
          </p>
        )}
        <table className="wq-table">
          <thead>
            <tr>
              <th style={{ width: 84 }}>Segment</th>
              <th>Transcript evidence</th>
              <th>What it means for the meeting</th>
            </tr>
          </thead>
          <tbody>
            {evidence.map((item, idx) => (
              <tr key={idx}>
                <td className="st">
                  {[
                    item.segment_id !== null &&
                      item.segment_id !== undefined &&
                      `#${item.segment_id}`,
                    item.timestamp,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </td>
                <td>{item.quote_or_labeled_paraphrase}</td>
                <td>{item.interpretation}</td>
              </tr>
            ))}
            {databaseRows.map((item, idx) => (
              <tr key={idx}>
                <td className="st">Database</td>
                <td colSpan={2}>
                  {item.topic}: {item.data_says} (
                  {CONFIRMATION_LABELS[item.status] ?? item.status})
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <h2 className="wq-h">2 | The recommended opening</h2>
        <p>{opening}</p>
      </BriefPage>
      <BriefPage
        kicker="The personalized meeting playbook"
        title={`A ${metadata.meeting_duration_minutes}-minute agenda built from the booking conversation.`}
        footer={FOOTER}
        pageNumber={2}
      >
        <table className="wq-table">
          <thead>
            <tr>
              <th style={{ width: 78 }}>Time</th>
              <th>Seller action</th>
              <th>Key question</th>
              <th>Desired outcome</th>
            </tr>
          </thead>
          <tbody>
            {agenda.map(item => (
              <tr key={item.start_minute}>
                <td className="whitespace-nowrap">
                  {item.start_minute}-{item.end_minute} min
                </td>
                <td>{item.seller_action}</td>
                <td>{item.question_or_prompt}</td>
                <td>{item.desired_outcome}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <h2 className="wq-h">3 | Discovery questions</h2>
        <List items={questions} />
        <h2 className="wq-h">4 | Introduce the accelerator</h2>
        <p>
          {accelerator.name}: {transition}
        </p>
        <h2 className="wq-h">5 | Meeting success criterion</h2>
        <p>
          <strong>Win:</strong> {success.win_condition} <strong>Avoid:</strong>{' '}
          {success.avoid}
          {success.no_fit_outcome && (
            <>
              {' '}
              <strong>No fit:</strong> {success.no_fit_outcome}
            </>
          )}
        </p>
      </BriefPage>
      <BriefPage
        kicker="Seller coaching & next actions"
        title="Know what to say if the conversation changes."
        footer={FOOTER}
        pageNumber={3}
      >
        <h2 className="wq-h">6 | Objection handling and conversation pivots</h2>
        <table className="wq-table">
          <thead>
            <tr>
              <th style={{ width: '36%' }}>Prospect signal</th>
              <th>Recommended response</th>
            </tr>
          </thead>
          <tbody>
            {pivots.map((item, idx) => (
              <tr key={idx}>
                <td>{item.prospect_signal}</td>
                <td>{item.response}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <h2 className="wq-h">7 | Recommended closing language</h2>
        <p>{closing}</p>
        <h2 className="wq-h">8 | Follow-up plan after the meeting</h2>
        <ul className="wq-list">
          {followUp.map((item, idx) => (
            <li key={idx}>
              <strong>
                {item.status === 'confirmed' ? 'Confirmed' : 'Proposed'}:
              </strong>{' '}
              {item.task}
            </li>
          ))}
        </ul>
        <h2 className="wq-h">9 | Confidence and source notes</h2>
        <p className="wq-note">{notes.join(' ')}</p>
        {gaps.length > 0 && (
          <>
            <p className="wq-note">
              <strong>Blocking gaps:</strong>
            </p>
            <List items={gaps} />
          </>
        )}
        <p className="wq-note">
          Advisor review required before client-facing use.
        </p>
      </BriefPage>
      {additionalSections.length > 0 && (
        <BriefPage
          kicker="Additional sections"
          title="More from this client's prompt."
          footer={FOOTER}
          pageNumber={4}
        >
          {additionalSections.map((section, idx) => (
            <div key={idx} className="flex flex-col gap-1">
              <h2 className="wq-h">
                {10 + idx} | {section.title}
              </h2>
              <p className="whitespace-pre-line">{section.content}</p>
            </div>
          ))}
        </BriefPage>
      )}
    </>
  );
};
